import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { SEOBreadcrumb } from "@/components/SEOBreadcrumb";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  Search, 
  Users, 
  Star, 
  CheckCircle, 
  Shield, 
  Briefcase, 
  Building2, 
  Sparkles, 
  FileCheck,
  MessageCircle,
  Calendar,
  Heart,
  Filter,
  Send,
  BadgeCheck,
  Share2,
  CreditCard,
  Check,
  X
} from "lucide-react";

const HowItWorks = () => {
  const { t } = useTranslation();

  const seekerSteps = [
    { icon: Search, titleKey: "step1Title", descKey: "step1Desc" },
    { icon: Users, titleKey: "step2Title", descKey: "step2Desc" },
    { icon: MessageCircle, titleKey: "step3Title", descKey: "step3Desc" },
    { icon: Star, titleKey: "step4Title", descKey: "step4Desc" },
  ];

  const jobSeekerSteps = [
    { icon: Briefcase, titleKey: "step1Title", descKey: "step1Desc" },
    { icon: Filter, titleKey: "step2Title", descKey: "step2Desc" },
    { icon: Send, titleKey: "step3Title", descKey: "step3Desc" },
  ];

  const proSteps = [
    { icon: FileCheck, titleKey: "step1Title", descKey: "step1Desc" },
    { icon: Shield, titleKey: "step2Title", descKey: "step2Desc" },
    { icon: BadgeCheck, titleKey: "step3Title", descKey: "step3Desc" },
  ];

  // 14 feature rows matching SubscriptionPlans
  const featureRows = [
    { key: "verifiedProfile", base: true, pro: true, plus: true },
    { key: "searchVisibility", base: true, pro: true, plus: true },
    { key: "directContacts", base: true, pro: true, plus: true },
    { key: "verifiedReviews", base: true, pro: true, plus: true },
    { key: "partnerDiscounts", base: true, pro: true, plus: true },
    { key: "proBadge", base: false, pro: true, plus: true },
    { key: "priorityRanking", base: false, pro: true, plus: true },
    { key: "multilingualBio", base: false, pro: true, plus: true },
    { key: "coverMedia", base: false, pro: true, plus: true },
    { key: "plusBadge", base: false, pro: false, plus: true },
    { key: "companyPage", base: false, pro: false, plus: true },
    { key: "teamMembers", base: false, pro: false, plus: true },
    { key: "companyMultilingual", base: false, pro: false, plus: true },
    { key: "jobPostingCredits", base: false, pro: false, plus: true },
  ];

  const faqItems = ["q1", "q2", "q3", "q4", "q5", "q6"];

  const renderFeatureValue = (value: boolean) => {
    if (value === true) return <Check className="w-5 h-5 text-green-600 mx-auto" />;
    return <X className="w-5 h-5 text-muted-foreground/40 mx-auto" />;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title={t("howItWorksPage.seoTitle")}
        description={t("howItWorksPage.seoDescription")}
      />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-surface py-16 md:py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <SEOBreadcrumb items={[{ label: t('footer.howItWorks') }]} />
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-dark">
              {t("howItWorksPage.hero.title")}
            </h1>
            <p className="text-lg md:text-xl text-dark/80 max-w-2xl mx-auto leading-relaxed">
              {t("howItWorksPage.hero.subtitle")}
            </p>
          </div>
        </section>

        {/* In-page Navigation Bar */}
        <nav className="sticky top-[80px] z-40 bg-surface border-b-[3px] border-dark py-3 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center gap-3 overflow-x-auto scrollbar-hide whitespace-nowrap pb-1">
              <a 
                href="#trova-professionista"
                className="font-heading font-bold text-sm text-dark hover:text-primary transition-colors shrink-0 px-4 py-2 border-[3px] border-dark bg-card hover:bg-primary/20 shadow-[3px_3px_0px_#101322] hover:shadow-[2px_2px_0px_#101322] hover:translate-x-[1px] hover:translate-y-[1px]"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('trova-professionista')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Trova professionista
              </a>
              <a 
                href="#annunci-lavoro"
                className="font-heading font-bold text-sm text-dark hover:text-primary transition-colors shrink-0 px-4 py-2 border-[3px] border-dark bg-card hover:bg-primary/20 shadow-[3px_3px_0px_#101322] hover:shadow-[2px_2px_0px_#101322] hover:translate-x-[1px] hover:translate-y-[1px]"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('annunci-lavoro')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Annunci di lavoro
              </a>
              <a 
                href="#professionisti"
                className="font-heading font-bold text-sm text-dark hover:text-primary transition-colors shrink-0 px-4 py-2 border-[3px] border-dark bg-card hover:bg-primary/20 shadow-[3px_3px_0px_#101322] hover:shadow-[2px_2px_0px_#101322] hover:translate-x-[1px] hover:translate-y-[1px]"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('professionisti')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Sei un professionista
              </a>
              <a 
                href="#faq"
                className="font-heading font-bold text-sm text-dark hover:text-primary transition-colors shrink-0 px-4 py-2 border-[3px] border-dark bg-card hover:bg-primary/20 shadow-[3px_3px_0px_#101322] hover:shadow-[2px_2px_0px_#101322] hover:translate-x-[1px] hover:translate-y-[1px]"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                FAQ
              </a>
              <a 
                href="#inizia"
                className="font-heading font-bold text-sm text-dark hover:text-card transition-colors shrink-0 px-4 py-2 border-[3px] border-dark bg-primary hover:bg-primary/80 shadow-[3px_3px_0px_#101322] hover:shadow-[2px_2px_0px_#101322] hover:translate-x-[1px] hover:translate-y-[1px]"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('inizia')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Inizia
              </a>
            </div>
          </div>
        </nav>

        {/* SECTION 1: For Seekers */}
        <section id="trova-professionista" className="scroll-mt-[140px] py-12 md:py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <BrutalCard className="bg-card">
              <div className="space-y-12">
                <div className="text-center max-w-3xl mx-auto">
                  <div className="inline-flex items-center gap-2 bg-primary/20 border-[3px] border-dark px-4 py-2 mb-6">
                    <Search className="w-5 h-5 text-primary" />
                    <span className="font-heading font-bold text-dark">
                      {t("howItWorksPage.tabs.seekers")}
                    </span>
                  </div>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-dark">
                    {t("howItWorksPage.forSeekers.title")}
                  </h2>
                  <p className="text-lg text-dark/70">
                    {t("howItWorksPage.forSeekers.subtitle")}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {seekerSteps.map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <div key={step.titleKey} className="bg-surface border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-8 text-center relative">
                        <div className="absolute -top-4 -left-4 w-10 h-10 bg-primary border-[3px] border-dark shadow-[3px_3px_0px_#101322] flex items-center justify-center font-heading font-bold text-dark text-lg">
                          {index + 1}
                        </div>
                        <div className="w-16 h-16 bg-primary/20 border-[3px] border-dark shadow-[4px_4px_0px_#101322] flex items-center justify-center mx-auto mb-4 mt-4">
                          <Icon className="w-8 h-8 text-primary" />
                        </div>
                        <h3 className="font-heading text-xl font-bold mb-3 text-dark">
                          {t(`howItWorksPage.forSeekers.${step.titleKey}`)}
                        </h3>
                        <p className="text-dark/70 text-sm leading-relaxed">
                          {t(`howItWorksPage.forSeekers.${step.descKey}`)}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-primary/10 border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary border-[3px] border-dark shadow-[3px_3px_0px_#101322] flex items-center justify-center shrink-0">
                        <Heart className="w-6 h-6 text-dark" />
                      </div>
                      <div>
                        <h3 className="font-heading text-xl font-bold mb-2 text-dark">
                          {t("howItWorksPage.forSeekers.freeAccessTitle")}
                        </h3>
                        <p className="text-dark/70">
                          {t("howItWorksPage.forSeekers.freeAccessDesc")}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-surface border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary border-[3px] border-dark shadow-[3px_3px_0px_#101322] flex items-center justify-center shrink-0">
                        <Calendar className="w-6 h-6 text-dark" />
                      </div>
                      <div>
                        <h3 className="font-heading text-xl font-bold mb-2 text-dark">
                          {t("howItWorksPage.forSeekers.eventsTitle")}
                        </h3>
                        <p className="text-dark/70">
                          {t("howItWorksPage.forSeekers.eventsDesc")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <Link to="/professionals">
                    <BrutalButton size="lg">
                      <Search className="w-5 h-5 mr-2" />
                      {t("howItWorksPage.forSeekers.cta")}
                    </BrutalButton>
                  </Link>
                </div>
              </div>
            </BrutalCard>
          </div>
        </section>

        {/* SECTION 2: For Job Seekers */}
        <section id="annunci-lavoro" className="scroll-mt-[140px] py-12 md:py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <BrutalCard className="bg-card">
              <div className="space-y-12">
                <div className="text-center max-w-3xl mx-auto">
                  <div className="inline-flex items-center gap-2 bg-primary/20 border-[3px] border-dark px-4 py-2 mb-6">
                    <Briefcase className="w-5 h-5 text-primary" />
                    <span className="font-heading font-bold text-dark">
                      {t("howItWorksPage.tabs.jobSeekers")}
                    </span>
                  </div>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-dark">
                    {t("howItWorksPage.forJobSeekers.title")}
                  </h2>
                  <p className="text-lg text-dark/70">
                    {t("howItWorksPage.forJobSeekers.subtitle")}
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  {jobSeekerSteps.map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <div key={step.titleKey} className="bg-surface border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-8 text-center relative">
                        <div className="absolute -top-4 -left-4 w-10 h-10 bg-primary border-[3px] border-dark shadow-[3px_3px_0px_#101322] flex items-center justify-center font-heading font-bold text-dark text-lg">
                          {index + 1}
                        </div>
                        <div className="w-16 h-16 bg-primary/20 border-[3px] border-dark shadow-[4px_4px_0px_#101322] flex items-center justify-center mx-auto mb-4 mt-4">
                          <Icon className="w-8 h-8 text-primary" />
                        </div>
                        <h3 className="font-heading text-xl font-bold mb-3 text-dark">
                          {t(`howItWorksPage.forJobSeekers.${step.titleKey}`)}
                        </h3>
                        <p className="text-dark/70 text-sm leading-relaxed">
                          {t(`howItWorksPage.forJobSeekers.${step.descKey}`)}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-primary/10 border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-8">
                  <h3 className="font-heading text-2xl font-bold mb-6 text-dark text-center">
                    {t("howItWorksPage.forJobSeekers.benefitsTitle")}
                  </h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {["benefit1", "benefit2", "benefit3", "benefit4"].map((key) => (
                      <div key={key} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-dark/80 text-sm">
                          {t(`howItWorksPage.forJobSeekers.${key}`)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-center">
                  <Link to="/jobs">
                    <BrutalButton size="lg">
                      <Briefcase className="w-5 h-5 mr-2" />
                      {t("howItWorksPage.forJobSeekers.cta")}
                    </BrutalButton>
                  </Link>
                </div>
              </div>
            </BrutalCard>
          </div>
        </section>

        {/* SECTION 3: For Professionals */}
        <section id="professionisti" className="scroll-mt-[140px] py-12 md:py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <BrutalCard className="bg-card">
              <div className="space-y-16">
                <div className="text-center max-w-3xl mx-auto">
                  <div className="inline-flex items-center gap-2 bg-surface border-[3px] border-dark px-4 py-2 mb-6">
                    <Building2 className="w-5 h-5 text-primary" />
                    <span className="font-heading font-bold text-dark">
                      {t("howItWorksPage.tabs.professionals")}
                    </span>
                  </div>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-dark">
                    {t("howItWorksPage.forProfessionals.title")}
                  </h2>
                  <p className="text-lg text-dark/70">
                    {t("howItWorksPage.forProfessionals.subtitle")}
                  </p>
                </div>

                {/* Screening Process */}
                <div>
                  <div className="text-center mb-8">
                    <h3 className="font-heading text-2xl font-bold text-dark">
                      {t("howItWorksPage.forProfessionals.screeningTitle")}
                    </h3>
                    <p className="text-dark/70 mt-2">
                      {t("howItWorksPage.forProfessionals.screeningSubtitle")}
                    </p>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    {proSteps.map((step, index) => {
                      const Icon = step.icon;
                      return (
                        <div key={step.titleKey} className="bg-surface border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-8 relative">
                          <div className="absolute -top-4 -left-4 w-10 h-10 bg-primary border-[3px] border-dark shadow-[3px_3px_0px_#101322] flex items-center justify-center font-heading font-bold text-dark text-lg">
                            {index + 1}
                          </div>
                          <div className="flex items-start gap-4 mt-2">
                            <div className="w-12 h-12 bg-primary/20 border-[3px] border-dark shadow-[3px_3px_0px_#101322] flex items-center justify-center shrink-0">
                              <Icon className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <h4 className="font-heading text-lg font-bold mb-2 text-dark">
                                {t(`howItWorksPage.forProfessionals.${step.titleKey}`)}
                              </h4>
                              <p className="text-dark/70 text-sm leading-relaxed">
                                {t(`howItWorksPage.forProfessionals.${step.descKey}`)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Plans Comparison */}
                <div className="bg-surface border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 md:p-8">
                  <div className="text-center mb-8">
                    <h3 className="font-heading text-2xl font-bold text-dark">
                      {t("howItWorksPage.forProfessionals.plansTitle")}
                    </h3>
                    <p className="text-dark/70 mt-2">
                      {t("howItWorksPage.forProfessionals.plansSubtitle")}
                    </p>
                  </div>
                  
                  {/* Features Table */}
                  <div className="overflow-x-auto pt-6">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-[3px] border-dark">
                          <th className="text-left py-3 px-4 font-heading">{t("subscriptionPlans.table.feature")}</th>
                          <th className="text-center py-3 px-4 font-heading">Base</th>
                          <th className="text-center py-3 px-4 font-heading relative">
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary px-3 py-1 border-2 border-dark text-xs font-bold whitespace-nowrap">
                              {t("pricing.plans.popular")}
                            </div>
                            <span className="bg-primary/10 px-2 py-1">Pro</span>
                          </th>
                          <th className="text-center py-3 px-4 font-heading">Plus</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-dark/20">
                        {featureRows.map((row) => (
                          <tr key={row.key}>
                            <td className="py-3 px-4 text-sm font-medium">{t(`subscriptionPlans.table.features.${row.key}.label`)}</td>
                            <td className="py-3 px-4 text-center">{renderFeatureValue(row.base)}</td>
                            <td className="py-3 px-4 text-center bg-primary/5">{renderFeatureValue(row.pro)}</td>
                            <td className="py-3 px-4 text-center">{renderFeatureValue(row.plus)}</td>
                          </tr>
                        ))}
                        {/* Price row */}
                        <tr className="border-t-[3px] border-dark bg-surface">
                          <td className="py-4 px-4 font-heading font-bold">{t("subscriptionPlans.table.price")}</td>
                          <td className="py-4 px-4 text-center font-bold text-lg">€70</td>
                          <td className="py-4 px-4 text-center font-bold text-lg bg-primary/10">€95</td>
                          <td className="py-4 px-4 text-center font-bold text-lg">€185</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="text-center mt-8">
                    <Link to="/subscription-plans">
                      <BrutalButton variant="outline">
                        {t("howItWorksPage.forProfessionals.ctaPlans")}
                      </BrutalButton>
                    </Link>
                  </div>
                </div>

                {/* Visibility Packages */}
                <div>
                  <div className="text-center mb-8">
                    <h3 className="font-heading text-2xl font-bold text-dark">
                      {t("howItWorksPage.forProfessionals.visibilityTitle")}
                    </h3>
                    <p className="text-dark/70 mt-2">
                      {t("howItWorksPage.forProfessionals.visibilitySubtitle")}
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-primary/10 border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-primary border-[3px] border-dark shadow-[4px_4px_0px_#101322] flex items-center justify-center shrink-0">
                          <Sparkles className="w-7 h-7 text-dark" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-heading text-xl font-bold text-dark">
                              {t("howItWorksPage.forProfessionals.spotlightName")}
                            </h4>
                            <span className="text-lg font-bold text-dark">
                              {t("howItWorksPage.forProfessionals.spotlightPrice")}
                            </span>
                          </div>
                          <p className="text-dark/70 text-sm">
                            {t("howItWorksPage.forProfessionals.spotlightDesc")}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-surface border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-primary border-[3px] border-dark shadow-[4px_4px_0px_#101322] flex items-center justify-center shrink-0">
                          <Share2 className="w-7 h-7 text-dark" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-heading text-xl font-bold text-dark">
                              {t("howItWorksPage.forProfessionals.socialBoostName")}
                            </h4>
                            <span className="text-lg font-bold text-dark">
                              {t("howItWorksPage.forProfessionals.socialBoostPrice")}
                            </span>
                          </div>
                          <p className="text-dark/70 text-sm">
                            {t("howItWorksPage.forProfessionals.socialBoostDesc")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-6">
                    <Link to="/visibility-packages">
                      <BrutalButton variant="outline">
                        {t("howItWorksPage.forProfessionals.ctaVisibility")}
                      </BrutalButton>
                    </Link>
                  </div>
                </div>

                {/* Job Postings */}
                <div className="bg-primary/10 border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-8">
                  <div className="flex flex-col md:flex-row items-center gap-6">
                    <div className="w-16 h-16 bg-primary border-[3px] border-dark shadow-[4px_4px_0px_#101322] flex items-center justify-center shrink-0">
                      <CreditCard className="w-8 h-8 text-dark" />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="font-heading text-xl font-bold text-dark mb-2">
                        {t("howItWorksPage.forProfessionals.jobPostingsTitle")}
                      </h3>
                      <p className="text-dark/70">
                        {t("howItWorksPage.forProfessionals.jobPostingsDesc")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <Link to="/become-professional">
                    <BrutalButton size="lg">
                      <Shield className="w-5 h-5 mr-2" />
                      {t("howItWorksPage.forProfessionals.cta")}
                    </BrutalButton>
                  </Link>
                </div>
              </div>
            </BrutalCard>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="scroll-mt-[140px] py-12 md:py-16 px-4 bg-surface">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl font-bold mb-8 text-center text-dark">
              {t("howItWorksPage.faq.title")}
            </h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item) => (
                <AccordionItem 
                  key={item} 
                  value={item}
                  className="bg-card border-[3px] border-dark shadow-[4px_4px_0px_#101322] px-6 data-[state=open]:shadow-[2px_2px_0px_#101322]"
                >
                  <AccordionTrigger className="font-heading font-bold text-left hover:no-underline py-4">
                    {t(`howItWorksPage.faq.${item}`)}
                  </AccordionTrigger>
                  <AccordionContent className="text-dark/70 pb-4">
                    {t(`howItWorksPage.faq.a${item.slice(1)}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Final CTA Section */}
        <section id="inizia" className="scroll-mt-[140px] py-12 md:py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl font-bold mb-8 text-center text-dark">
              {t("howItWorksPage.finalCta.title")}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <BrutalCard className="text-center">
                <div className="w-16 h-16 bg-primary/20 border-[3px] border-dark shadow-[4px_4px_0px_#101322] flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <p className="text-dark/70 mb-6">
                  {t("howItWorksPage.finalCta.userDesc")}
                </p>
                <Link to="/professionals">
                  <BrutalButton className="w-full">
                    {t("howItWorksPage.finalCta.userCta")}
                  </BrutalButton>
                </Link>
              </BrutalCard>
              <BrutalCard className="text-center bg-primary/10">
                <div className="w-16 h-16 bg-primary/20 border-[3px] border-dark shadow-[4px_4px_0px_#101322] flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
                <p className="text-dark/70 mb-6">
                  {t("howItWorksPage.finalCta.proDesc")}
                </p>
                <Link to="/become-professional">
                  <BrutalButton className="w-full">
                    {t("howItWorksPage.finalCta.proCta")}
                  </BrutalButton>
                </Link>
              </BrutalCard>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HowItWorks;
